import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement, PopulationDensityBenchmark } from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { NCCardSectionHeader, printLatestDate } from '@/components/NCCard/NCCard';
import { getNCChangeIcon, getNCChangeIconVariant, NCFactCapsule } from '@/components/NCFactCapsule/NCFactCapsule';
import { BenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { getBenchmarksFromFacts, useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';

export type PopulationDensityStatsTileProps = {
  latestAnalysisFact: PlotReportFactElement<number | null> | undefined;
  benchmarkFact: {
    urbanCenter: PlotReportFactElement<GraphFact[] | null> | undefined;
    urbanCluster: PlotReportFactElement<GraphFact[] | null> | undefined;
    rural: PlotReportFactElement<GraphFact[] | null> | undefined;
  };
};

export const PopulationDensityStatsTile = ({ latestAnalysisFact, benchmarkFact }: PopulationDensityStatsTileProps) => {
  const { t } = useTranslation();
  const lastMeasuredDate = printLatestDate([latestAnalysisFact?.measured_at]);

  const benchmarks = getBenchmarksFromFacts({
    highBenchmarkFact: benchmarkFact.urbanCenter,
    moderateBenchmarkFact: benchmarkFact.urbanCluster,
    lowBenchmarkFact: benchmarkFact.rural,
  });

  const benchmarkType = useBenchmarkForFact<PopulationDensityBenchmark>(benchmarks, latestAnalysisFact?.value, [
    PopulationDensityBenchmark.rural,
    PopulationDensityBenchmark.urban_cluster,
    PopulationDensityBenchmark.urban_center,
  ]);

  return (
    <Stack className='justify-center gap-8 p-6 lg:gap-4 lg:p-8 lg:pr-2' data-testid='population-density-stats-tile'>
      <div className='flex flex-col gap-8 lg:gap-4'>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.details.populationDensity.labels.populationDensity')}
          supplementaryInfo={`${t('shared.ncaDetail.details.labels.updated')} ${lastMeasuredDate}`}
          popover={{
            title: t('shared.ncaDetail.details.populationDensity.explainers.populationDensity.title'),
            body: t('shared.ncaDetail.details.populationDensity.explainers.populationDensity.body'),
          }}
        />
        <NCFactCapsule
          label={t('shared.ncaDetail.details.labels.latestAnalysis')}
          fact={latestAnalysisFact}
          capsuleIconProps={{
            icon: getNCChangeIcon(latestAnalysisFact),
            variant: getNCChangeIconVariant(latestAnalysisFact),
          }}
          infoPopoverProps={{
            title: t('shared.ncaDetail.details.explainers.latestAnalysis.title'),
            body: t('shared.ncaDetail.details.explainers.latestAnalysis.body', { date: lastMeasuredDate }),
            popoverTriggerProps: {
              className: 'text-xs',
            },
          }}
        />
        {benchmarkType && (
          <Stack className='mt-4 gap-4 rounded-2xl bg-neutral-black-4 px-4 py-6'>
            <BenchmarkPill benchmarkType={benchmarkType} />
            <span className='typography-body1Bold'>
              {t('shared.ncaDetail.details.populationDensity.explainers.whatDoesItMean.title')}
            </span>
            <span className='typography-body1 text-text-secondary'>
              {t(`shared.ncaDetail.details.populationDensity.explainers.whatDoesItMean.benchmark.${benchmarkType}`)}
            </span>
          </Stack>
        )}
      </div>
    </Stack>
  );
};
